import Waveform from './Waveform.jsx';
import SoundAvatar from './SoundAvatar.jsx';
import formatClipTitle from '../../utils/formatClipTitle.js';
import '../../styles/theme.css';

/**
 * Host-scale waveform visualizer for duel reveal.
 * Matches design/canvas/HostReveal.dc.html side panels.
 *
 * Shows player signature waveform (not emoji), clip label + duration,
 * large waveform envelope with playhead, and vote progress bar.
 */
export function WaveformVisualizer({
  isPlaying = false,
  progress = 0,
  duration = 3.0,
  playerName = '',
  playerAvatar = '🎧',
  clipTitle = '',
  clip = null,
  barsCount = 56,
  height = 72,
  color = '#3ACFCF',
  onAir = false,
}) {
  // The waveform is the CLIP's measured envelope. This used to be generated
  // from Math.sin/Math.cos, so every clip drew the same picture while looking
  // exactly like a real measurement — the precise failure mode the tree rules
  // forbid. With no clip, Waveform renders an honest empty state.
  const peaks = clip?.peaks || null;
  const shownDuration = clip?.duration || duration;
  const currentTime = (progress * Number(shownDuration)).toFixed(1);
  const totalTime = Number(shownDuration).toFixed(1);

  const isActive = isPlaying || onAir;
  const accentColor = color || '#3ACFCF';

  return (
    <div
      data-testid="waveform-visualizer"
      data-onair={isActive ? 'true' : 'false'}
      style={{
        background: isActive ? 'rgba(255, 255, 255, 0.028)' : '#0D1015',
        border: `1px solid ${isActive ? accentColor : '#1C2229'}`,
        borderRadius: '20px',
        padding: '30px 32px',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        minHeight: '200px',
      }}
    >
      {/* Player Header Info — signature waveform + name + On Air tag */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flex: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
          <SoundAvatar
            size="sig"
            seed={typeof playerAvatar === 'string' ? playerAvatar : playerName}
            clip={typeof playerAvatar === 'object' ? playerAvatar : null}
            style={{
              width: '52px',
              height: '26px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              flex: 'none',
            }}
          />
          <span
            style={{
              fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
              fontSize: '24px',
              fontWeight: 800,
              letterSpacing: '-0.022em',
              color: isActive ? '#EDEFF2' : '#8A939C',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {playerName}
          </span>
        </div>

        <span
          style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: isActive ? accentColor : '#6E7982',
            flex: 'none',
          }}
        >
          {isActive ? 'On air' : 'Up next'}
        </span>
      </div>

      {/* Clip label + duration + waveform envelope */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: 0 }}>
        {clipTitle && formatClipTitle(clipTitle) !== formatClipTitle(playerName) && (
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', minWidth: 0 }}>
            <span
              title={typeof clipTitle === 'string' ? clipTitle : undefined}
              style={{
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                color: isActive ? '#C6CDD4' : '#69737C',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: '1 1 0',
              }}
            >
              {formatClipTitle(clipTitle)}
            </span>
            <span
              style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '11px',
                color: '#6E7982',
                fontVariantNumeric: 'tabular-nums',
                flex: 'none',
              }}
            >
              {totalTime}s
            </span>
          </div>
        )}

        {/* The clip's OWN waveform, distinct from the player's SoundAvatar
            waveform in the header above — both render data-testid="waveform",
            so a test targeting the clip needs this wrapper to say which. */}
        <div data-testid="clip-waveform">
        <Waveform
          peaks={peaks}
          seed={clip?.id || clipTitle || playerName || 'visualizer'}
          bars={barsCount}
          height={typeof height === 'number' ? height : 72}
          progress={isActive ? Math.min(1, Math.max(0, progress)) : null}
          showPlayhead={isActive}
          color={accentColor}
          dimColor={isActive ? '#2B333A' : '#1E252B'}
          ariaLabel={clipTitle ? `Waveform of ${clipTitle}` : 'Waveform'}
        />
        </div>
      </div>

      {/* Bottom Progress Bar + Time */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          paddingTop: '20px',
          borderTop: `1px solid ${isActive ? 'rgba(255, 255, 255, 0.09)' : '#1C2229'}`,
          flex: 'none',
        }}
      >
        <div
          style={{
            flex: '1 1 0',
            height: '8px',
            borderRadius: '4px',
            background: '#14181D',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, (progress || 0) * 100))}%`,
              height: '100%',
              borderRadius: '4px',
              background: accentColor,
              transition: 'width 0.1s linear',
            }}
          />
        </div>
        <span
          style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '15px',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: isActive ? '#EDEFF2' : '#69737C',
            flex: 'none',
            minWidth: '62px',
            textAlign: 'right',
          }}
        >
          {currentTime}s / {totalTime}s
        </span>
      </div>
    </div>
  );
}

export default WaveformVisualizer;
